import React from 'react';
import { View, Pressable, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { Icon } from '@ant-design/react-native';
import { Text } from '@shared/components/text';
import { useI18n } from '@shared/i18n/useI18n';
import { colorPalette } from '@shared/config/colorsConfig';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { useChannelTitle } from '../hooks/useChannelTitle';

const HEADER_COLORS = {
    background: colorPalette.white,
    text: colorPalette.black,
    border: colorPalette.gray[200],
    shadow: colorPalette.black,
} as const;

const HEADER_DIMENSIONS = {
    paddingTop: 10,
    paddingBottom: 10,
    paddingHorizontal: 16,
    minHeight: 60,
    buttonPadding: 8,
    buttonRadius: 20,
    iconSize: 20,
} as const;

interface CustomChatHeaderProps {
    channel: GroupChannel | null;
    fallbackTitle?: string;
    onPressLeft?: () => void;
}

export const CustomChatHeader: React.FC<CustomChatHeaderProps> = ({
    channel,
    fallbackTitle,
    onPressLeft,
}) => {
    const { t } = useI18n();
    const title = useChannelTitle(channel);
    const navigation = useNavigation<any>();
    const insets = useSafeAreaInsets();
    const styles = createStyles(insets);

    const handleBackPress = () => {
        if (onPressLeft) {
            onPressLeft();
        } else {
            navigation.goBack();
        }
    };



    return (
        <View style={styles.header}>
                <Pressable
                    onPress={handleBackPress}
                    style={styles.headerButton}
                >
                    <View style={styles.circleButton}>
                        <Icon
                            name="left"
                            size={HEADER_DIMENSIONS.iconSize}
                            color={HEADER_COLORS.text}
                        />
                    </View>
                </Pressable>

            <Text numberOfLines={1} className="text-xl font-semibold flex-1 min-w-0 ml-4 text-left">
                {title || fallbackTitle || t('screens.chat.title')}
            </Text>

        </View>
    );
};

const createStyles = (insets: { top: number }) => StyleSheet.create({
    header: {
        backgroundColor: HEADER_COLORS.background,
        paddingTop: insets.top + HEADER_DIMENSIONS.paddingTop,
        paddingBottom: HEADER_DIMENSIONS.paddingBottom,
        paddingHorizontal: HEADER_DIMENSIONS.paddingHorizontal,
        borderBottomWidth: 1,
        borderBottomColor: HEADER_COLORS.border,
        shadowColor: HEADER_COLORS.shadow,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 3.84,
        elevation: 2,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: HEADER_DIMENSIONS.minHeight,
    },

    headerButton: {
        padding: 0,
        backgroundColor: 'transparent',
    },

    circleButton: {
        width: 40,
        height: 40,
        borderRadius: 25,
        borderColor: colorPalette.gray[300],
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
});
